/**
 * Словарь аффирмаций на русском: темы, ключевые слова и фразы.
 *
 * Ключи:
 *   "тревог"      — начало слова (тревога, тревожусь, тревожность)
 *   "=вес"        — слово целиком (чтобы не ловить «весна», «весело»)
 *   "люблю себя"  — фраза с пробелом ищется как подстрока
 * Фразы: "{м|ж}" — мужской и женский вариант; для обращения без рода такие фразы пропускаются.
 * Фразы общие для всех пользователей — один раз озвученная фраза дальше берётся из кэша.
 */

export interface ThemeDef {
  id: string;
  title: string;
  keys: string[];
  phrases: string[];
  /** близкие темы: из них добираем фразы, когда своя тема исчерпана */
  related?: string[];
}

export const THEME_DEFS: ThemeDef[] = [
  {
    id: "calm",
    related: ["present", "letgo", "sleep"],
    title: "Спокойствие",
    keys: ["тревог", "тревож", "беспоко", "стресс", "нерв", "паник", "волну", "спокой", "напряж", "пережива", "успоко", "расслаб", "умиротвор", "сует", "раздраж", "накрыва"],
    phrases: [
      "Я в безопасности здесь и сейчас",
      "С каждым вдохом я становлюсь спокойнее",
      "Спокойствие наполняет моё тело и ум",
      "Я {спокоен|спокойна} и {собран|собрана}",
      "Я отпускаю то, что не могу контролировать",
      "Мои мысли замедляются, моё дыхание становится глубже",
      "Я позволяю себе отдохнуть от забот",
      "Внутри меня есть тихое место, и я всегда могу туда вернуться",
      "Я справляюсь с любой ситуацией шаг за шагом",
      "Моё тело расслабляется, плечи опускаются",
      "Я выбираю мир вместо тревоги",
      "Всё, что происходит, я встречаю спокойно",
      "Я доверяю жизни и отпускаю напряжение",
      "Тишина внутри меня сильнее шума снаружи",
    ],
  },
  {
    id: "fear",
    related: ["confidence", "change", "calm"],
    title: "Смелость",
    keys: ["страх", "боюс", "бояться", "боязн", "испуг", "страшн", "смел", "решимост", "решиться", "отваж", "храбр", "рискну", "риск"],
    phrases: [
      "Я действую, даже когда мне страшно",
      "Смелость растёт во мне с каждым шагом",
      "Я сильнее своих страхов",
      "Я {готов|готова} к новому опыту",
      "Каждый мой шаг вперёд делает меня свободнее",
      "Я доверяю себе в неизвестности",
      "Мой страх — лишь чувство, и он проходит",
      "Я выбираю рост, а не привычный комфорт",
      "Я {смелый|смелая}, и это видно в моих делах",
      "Я встречаю новое с любопытством",
    ],
  },
  {
    id: "confidence",
    related: ["selfworth", "fear", "speaking"],
    title: "Уверенность",
    keys: ["уверен", "неуверен", "сомнева", "сомнен", "стесн", "робе", "робост", "застенчив", "постоять за себя", "самоуверен", "увереннее"],
    phrases: [
      "Я доверяю себе и своим решениям",
      "Мой голос важен, и меня слышат",
      "Я {уверен|уверена} в себе",
      "С каждым днём я становлюсь увереннее",
      "Я спокойно говорю о том, чего хочу",
      "Я занимаю своё место в этом мире",
      "Я опираюсь на свой опыт и свои силы",
      "Мне не нужно ничьё разрешение, чтобы быть собой",
      "Я держусь прямо и дышу свободно",
      "Мои решения рождаются из ясности",
      "Я принимаю комплименты с благодарностью",
      "Я {достоин|достойна} уважения",
    ],
  },
  {
    id: "speaking",
    related: ["confidence", "calm", "fear"],
    title: "Выступления",
    keys: ["выступ", "публичн", "презентац", "доклад", "сцен", "речь", "аудитор", "эфир", "вебинар", "говорить перед", "конференц"],
    phrases: [
      "Я спокойно выхожу к людям",
      "Мой голос звучит ровно и уверенно",
      "Я рад{|а} делиться тем, что знаю",
      "Слушатели настроены ко мне доброжелательно",
      "Я легко нахожу нужные слова",
      "Паузы в моей речи звучат уверенно",
      "Я говорю от сердца, и меня слышат",
      "Волнение превращается в энергию выступления",
      "Я помню главное и говорю о главном",
      "После выступления я благодарю себя за смелость",
    ],
  },
  {
    id: "selfworth",
    related: ["confidence", "femininity", "letgo"],
    title: "Принятие себя",
    keys: ["достоин", "достойн", "ценност", "ценить себя", "самооценк", "принять себя", "принимать себя", "люблю себя", "любить себя", "полюбить себя", "=вина", "=вину", "чувство вины", "виноват", "стыд", "недостаточн", "хуже других", "критик", "самокритик", "сравнива", "неудачни", "никчем"],
    phrases: [
      "Я принимаю себя полностью",
      "Я отношусь к себе с теплом и уважением",
      "Мои чувства важны",
      "Я {достоин|достойна} лучшего",
      "Я {достаточно хорош|достаточно хороша}",
      "Я говорю с собой так же бережно, как с близким человеком",
      "Мои ошибки — часть моего пути, и я учусь на них",
      "Я ценю себя не за заслуги, а просто так",
      "Я больше не сравниваю себя с другими",
      "Я прощаю себя и иду дальше",
      "Я — {важный|важная} человек в своей жизни",
      "Я разрешаю себе быть неидеальн{ым|ой}",
      "Я замечаю свои успехи и радуюсь им",
    ],
  },
  {
    id: "femininity",
    related: ["selfworth", "body", "love"],
    title: "Женственность",
    keys: ["женствен", "красот", "красив", "привлекател", "сексуальн", "мягкост", "нежност", "обаян", "харизм", "сияни", "чувствуюсь женщиной", "женщин"],
    phrases: [
      "Я наслаждаюсь своей женственностью",
      "Моя красота светится изнутри",
      "Я мягкая и сильная одновременно",
      "Я позволяю себе быть нежной",
      "Мне приятно заботиться о себе",
      "Я притягиваю восхищённые взгляды",
      "Моё тело — источник удовольствия и радости",
      "Я сияю, когда я в гармонии с собой",
      "Я принимаю заботу и подарки с лёгкостью",
      "Я чувствую себя красивой каждый день",
    ],
  },
  {
    id: "money",
    related: ["business", "career", "goals"],
    title: "Изобилие",
    keys: ["деньг", "денег", "денеж", "доход", "зарабат", "заработ", "финанс", "богат", "изобил", "зарплат", "=долг", "=долги", "долгов", "долгам", "кредит", "бедн", "нищ", "достаток", "процвета", "инвест", "накоп", "сбереж", "миллион", "благосостоян"],
    phrases: [
      "Деньги приходят ко мне легко и регулярно",
      "Мой доход растёт вместе с моей ценностью",
      "Я {открыт|открыта} новым источникам дохода",
      "Я разумно управляю своими финансами",
      "Я {достоин|достойна} изобилия",
      "Я спокойно отношусь к деньгам и легко с ними обращаюсь",
      "Мои долги уменьшаются, мой запас растёт",
      "Я с радостью трачу и с радостью получаю",
      "Я замечаю возможности заработать",
      "Изобилие — естественное состояние моей жизни",
      "Я благодарю за каждый рубль, который приходит ко мне",
      "Я позволяю себе жить в достатке",
      "Мои знания и время хорошо оплачиваются",
    ],
  },
  {
    id: "business",
    related: ["money", "goals", "focus"],
    title: "Своё дело",
    keys: ["бизнес", "клиент", "продаж", "продава", "заказ", "заказчик", "предпринимат", "стартап", "свое дело", "своё дело", "моя компания", "мою компанию", "проект", "фриланс", "=ип", "выручк", "прибыл"],
    phrases: [
      "Мои клиенты находят меня сами",
      "Я с удовольствием предлагаю то, что создаю",
      "Моё дело растёт стабильно и спокойно",
      "Я ценю свою работу и называю достойную цену",
      "Ко мне приходят люди, которым я действительно {нужен|нужна}",
      "Я принимаю решения быстро и ясно",
      "Каждый день мой проект становится сильнее",
      "Я легко договариваюсь и выполняю обещания",
      "Мои продажи растут, потому что я верю в свой продукт",
      "Я строю дело, которым горжусь",
    ],
  },
  {
    id: "career",
    related: ["goals", "confidence", "money"],
    title: "Работа и карьера",
    keys: ["работ", "карьер", "должност", "повышен", "начальн", "руководител", "коллег", "офис", "профессион", "специалист", "резюме", "вакансия", "ваканс", "увольн", "уволи"],
    phrases: [
      "Я делаю работу, которая меня вдохновляет",
      "Мои усилия замечают и ценят",
      "Я расту в своей профессии каждый день",
      "Я {открыт|открыта} новым возможностям в карьере",
      "Я спокойно прошу то, что мне положено",
      "Я легко нахожу общий язык с коллегами",
      "Моя работа приносит пользу и радость",
      "Я заслуживаю повышения и {готов|готова} к нему",
      "Я на своём месте",
      "Я соблюдаю баланс между работой и отдыхом",
    ],
  },
  {
    id: "interview",
    related: ["confidence", "career", "calm", "speaking"],
    title: "Собеседование",
    keys: ["собеседован", "интервью", "новую работу", "новой работ", "найти работ", "новую компанию", "устроиться", "оффер", "отбор", "кастинг"],
    phrases: [
      "Я спокойно прохожу собеседование",
      "Мой опыт ценен, и я говорю о нём уверенно",
      "Я легко отвечаю на вопросы",
      "Я встречаю новых людей с открытостью",
      "Я выбираю работу так же, как работа выбирает меня",
      "Нужная работа уже ищет меня",
      "Я показываю себя {таким, какой|такой, какая} есть, и этого достаточно",
      "Я получаю предложение, которое меня радует",
      "Каждая встреча приближает меня к моей цели",
      "Я спокойно рассказываю о своих сильных сторонах",
      "Я задаю вопросы с интересом и достоинством",
      "Я знаю, что могу предложить",
      "Я прихожу на встречу {отдохнувшим и собранным|отдохнувшей и собранной}",
      "Я обсуждаю условия уверенно и спокойно",
    ],
  },
  {
    id: "focus",
    related: ["discipline", "goals", "energy"],
    title: "Фокус и продуктивность",
    keys: ["продуктив", "фокус", "сосредоточ", "концентрац", "внимани", "отвлека", "эффективн", "успева", "дедлайн", "многозадач", "делать важное", "планиров"],
    phrases: [
      "Я сосредоточенно и легко делаю важное",
      "Моё внимание там, где мои цели",
      "Я делаю одно дело за раз и делаю его хорошо",
      "У меня достаточно времени на то, что действительно важно",
      "Я легко вхожу в состояние потока",
      "Я заканчиваю начатое",
      "Я выбираю главное и спокойно отпускаю лишнее",
      "Мой день складывается ясно и продуктивно",
      "Я работаю с удовольствием и отдыхаю без вины",
    ],
  },
  {
    id: "discipline",
    related: ["focus", "goals", "energy"],
    title: "Дисциплина",
    keys: ["лень", "ленюсь", "ленив", "прокраст", "откладыва", "дисципл", "привычк", "мотивац", "силы воли", "сила воли", "регулярн", "начать", "бросаю"],
    phrases: [
      "Я начинаю сейчас, и это легко",
      "Каждый маленький шаг приближает меня к цели",
      "Мне нравится держать слово, данное себе",
      "Мои полезные привычки укрепляются каждый день",
      "Я делаю то, что решил{|а}, даже когда не хочется",
      "Я выбираю действие вместо откладывания",
      "Дисциплина — это моя забота о себе",
      "Я радуюсь каждому сделанному шагу",
      "Я {способен|способна} на большее, чем думаю",
    ],
  },
  {
    id: "goals",
    related: ["discipline", "confidence", "money"],
    title: "Цели и успех",
    keys: ["=цель", "=цели", "целей", "целям", "успех", "успеш", "достич", "достига", "результат", "мечт", "желани", "исполн", "реализ", "амбиц", "побед", "добиться", "добиваюсь"],
    phrases: [
      "Я двигаюсь к своей цели шаг за шагом",
      "Мои мечты сбываются в правильное время",
      "Я заслуживаю успеха",
      "Я вижу свою цель ясно",
      "Каждый день я делаю что-то для своей мечты",
      "Успех приходит ко мне естественно",
      "Я радуюсь своим победам, большим и маленьким",
      "Я верю в то, что задумал{|а}",
      "Препятствия делают меня сильнее",
      "Я создаю жизнь, о которой мечтаю",
    ],
  },
  {
    id: "love",
    related: ["femininity", "selfworth", "relationship"],
    title: "Любовь",
    keys: ["любв", "любов", "любим", "отношен", "партн", "одинок", "близост", "свидан", "вторую половин", "встретить", "замуж", "жених", "невест", "семью созда", "=пара", "=пару", "роман"],
    phrases: [
      "Я открываю сердце любви",
      "Я притягиваю тёплые и здоровые отношения",
      "Любовь легко входит в мою жизнь",
      "Я {достоин|достойна} любви и заботы",
      "Меня любят {таким, какой|такой, какая} я есть",
      "Мой человек уже идёт ко мне навстречу",
      "Я дарю любовь и с радостью её принимаю",
      "В моих отношениях есть уважение и нежность",
      "Я {готов|готова} к близости и доверию",
      "Я притягиваю того, кто ценит меня",
      "Я наслаждаюсь каждым днём, проведённым с любимым человеком",
    ],
  },
  {
    id: "relationship",
    related: ["love", "boundaries", "calm"],
    title: "Отношения в паре",
    keys: ["=муж", "=мужа", "=мужем", "=мужу", "=жена", "=жену", "=жены", "=женой", "супруг", "парень", "девушк", "ссор", "конфликт", "ревн", "измен", "доверя", "доверие", "брак", "расстава", "развод"],
    phrases: [
      "Мы слышим и понимаем друг друга",
      "Я говорю о своих чувствах спокойно и честно",
      "В наших отношениях много тепла",
      "Я доверяю и заслуживаю доверия",
      "Мы решаем разногласия бережно",
      "Каждый день я выбираю любовь",
      "Я благодарю своего партнёра за то, что он рядом",
      "Наши отношения становятся глубже и крепче",
      "Я сохраняю себя и в любви",
    ],
  },
  {
    id: "family",
    related: ["relationship", "calm", "gratitude"],
    title: "Семья и дети",
    keys: ["семь", "дети", "детей", "детьми", "детск", "ребен", "ребён", "сын", "доч", "мама", "мамы", "материнств", "отцовств", "родител", "беремен", "малыш", "декрет"],
    phrases: [
      "Моя семья — место любви и поддержки",
      "Я {терпелив|терпелива} и {спокоен|спокойна} с близкими",
      "Мои дети чувствуют мою любовь",
      "Я {хороший родитель|хорошая мама}, и я учусь каждый день",
      "В нашем доме тепло и спокойно",
      "Я нахожу время и для семьи, и для себя",
      "Я прощаю родителей и отпускаю старые обиды",
      "Я благодарю свой род за силу, которую получил{|а}",
      "Моему малышу хорошо и безопасно рядом со мной",
    ],
  },
  {
    id: "social",
    related: ["confidence", "boundaries", "joy"],
    title: "Общение и друзья",
    keys: ["друз", "дружб", "общени", "общать", "знакомств", "окружени", "люди вокруг", "одиночеств", "социальн", "коммуникац"],
    phrases: [
      "Я легко знакомлюсь с новыми людьми",
      "Меня окружают люди, которые меня поддерживают",
      "Мне интересно с людьми, а людям интересно со мной",
      "Я притягиваю искренних друзей",
      "Я спокойно говорю «нет», и мои отношения от этого крепнут",
      "Я дарю людям тепло, и оно возвращается ко мне",
      "Я на своей волне среди своих людей",
      "Я легко поддерживаю разговор",
    ],
  },
  {
    id: "boundaries",
    related: ["selfworth", "confidence", "calm"],
    title: "Личные границы",
    keys: ["границ", "отказ", "сказать нет", "говорить нет", "манипул", "давят", "токсич", "угожда", "обидч", "абьюз", "навязыв"],
    phrases: [
      "Я имею право говорить «нет»",
      "Мои границы важны, и я их берегу",
      "Я выбираю, кому открывать своё время и силы",
      "Я спокойно отказываю, без чувства вины",
      "Я не отвечаю за чужие ожидания",
      "Я окружаю себя людьми, которые уважают меня",
      "Моё «да» — свободное, моё «нет» — твёрдое",
      "Я защищаю свой покой",
    ],
  },
  {
    id: "health",
    related: ["energy", "body", "calm"],
    title: "Здоровье",
    keys: ["здоров", "болез", "болит", "боле", "лечени", "иммун", "выздорав", "восстанов", "самочувств", "врач", "диагноз", "давлени", "голов"],
    phrases: [
      "Моё тело знает, как восстанавливаться",
      "Я бережно забочусь о своём здоровье",
      "С каждым днём я чувствую себя всё лучше",
      "Каждая клетка моего тела наполняется силой",
      "Я прислушиваюсь к своему телу",
      "Я выбираю то, что поддерживает моё здоровье",
      "Моё дыхание наполняет меня жизнью",
      "Я доверяю своему телу и врачам, которые мне помогают",
      "Здоровье — моё естественное состояние",
    ],
  },
  {
    id: "energy",
    related: ["health", "joy", "morning"],
    title: "Энергия",
    keys: ["энерг", "бодр", "устал", "устаю", "сил ", "силы", "выгор", "апати", "вялост", "истощ", "ресурс", "тонус", "драйв", "вдохнов"],
    phrases: [
      "Я наполняюсь энергией с каждым вдохом",
      "Во мне достаточно сил для всего важного",
      "Я восстанавливаюсь быстро и легко",
      "Я трачу силы на то, что меня наполняет",
      "Я в ресурсе",
      "Моя энергия течёт свободно",
      "Я отдыхаю вовремя и без вины",
      "Каждое утро я просыпаюсь {бодрым|бодрой}",
      "Вдохновение приходит ко мне само",
    ],
  },
  {
    id: "sleep",
    related: ["calm", "present", "letgo"],
    title: "Глубокий сон",
    keys: ["=сон", "=сна", "=сну", "сплю", "спать", "бессон", "засып", "засну", "высып", "просып", "ночь", "ночью", "кошмар", "отбой", "перед сном"],
    phrases: [
      "Я легко засыпаю и глубоко сплю",
      "Ночь дарит мне отдых и восстановление",
      "Я отпускаю этот день с благодарностью",
      "Моё тело расслабляется, и сон приходит сам",
      "Мои мысли становятся тише и мягче",
      "Я в безопасности, и я могу отдыхать",
      "Каждый вдох делает меня спокойнее и тяжелее",
      "Утром я проснусь {отдохнувшим|отдохнувшей}",
      "Всё, что нужно, подождёт до утра",
      "Сон бережно восстанавливает меня",
    ],
  },
  {
    id: "body",
    related: ["health", "selfworth", "sport"],
    title: "Тело и форма",
    keys: ["тело", "тела", "телу", "=вес", "=веса", "=весом", "похуд", "фигур", "стройн", "лишний вес", "лишних кг", "питани", "диет", "=еда", "=еду", "=еды", "переед", "внешност", "отражени"],
    phrases: [
      "Я забочусь о своём теле с любовью",
      "Моё тело — мой друг и помощник",
      "Я выбираю еду, которая даёт мне силы",
      "Я благодарю своё тело за всё, что оно делает",
      "Мне приятно двигаться",
      "Я слышу, когда мне достаточно",
      "Я принимаю своё тело на каждом этапе пути",
      "Моё тело с каждым днём становится сильнее",
      "Я смотрю в зеркало с теплом",
    ],
  },
  {
    id: "sport",
    related: ["body", "energy", "discipline"],
    title: "Спорт",
    keys: ["спорт", "трениров", "=зал", "=зале", "=бег", "бегаю", "бегать", "йог", "фитнес", "растяжк", "марафон", "мышц", "выносливост", "соревнован"],
    phrases: [
      "Мне нравится чувствовать силу своего тела",
      "Каждая тренировка делает меня сильнее",
      "Я с радостью двигаюсь каждый день",
      "Я тренируюсь регулярно, и это легко",
      "Моё тело становится выносливее",
      "Я уважаю свой темп",
      "Я выхожу на тренировку, даже когда не хочется, и рад{|а} этому",
      "Спорт наполняет меня энергией",
    ],
  },
  {
    id: "study",
    related: ["focus", "calm", "confidence"],
    title: "Учёба и экзамены",
    keys: ["учёб", "учеб", "учусь", "учить", "экзамен", "сесси", "егэ", "огэ", "диплом", "университет", "универ", "школ", "курс", "обучени", "запомина", "знани", "язык", "английск"],
    phrases: [
      "Я легко запоминаю новое",
      "Мне интересно учиться",
      "На экзамене я {спокоен|спокойна} и всё вспоминаю",
      "Знания укладываются в моей голове ясно и надолго",
      "Я учусь каждый день понемногу",
      "Мой ум ясный и любознательный",
      "Я верю в свои способности",
      "Каждое занятие приближает меня к цели",
      "Я горжусь тем, сколько уже знаю",
    ],
  },
  {
    id: "creativity",
    related: ["joy", "confidence", "goals"],
    title: "Творчество",
    keys: ["творч", "креатив", "идеи", "идей", "рисова", "писать", "пишу", "музык", "дизайн", "искусств", "художн", "вдохновен", "творить", "блог", "контент"],
    phrases: [
      "Идеи приходят ко мне легко",
      "Я разрешаю себе творить без страха оценки",
      "Моё творчество ценно",
      "Я доверяю своему вкусу",
      "Я создаю каждый день",
      "Вдохновение живёт во мне",
      "Я делюсь своими работами с радостью",
      "Мой голос в творчестве уникален",
    ],
  },
  {
    id: "gratitude",
    related: ["joy", "present", "general"],
    title: "Благодарность",
    keys: ["благодар", "спасибо", "признател"],
    phrases: [
      "Я благодарю этот день",
      "Я замечаю хорошее вокруг себя",
      "Моя жизнь полна маленьких радостей",
      "Я благодар{ен|на} людям, которые рядом",
      "Каждый день мне есть за что сказать спасибо",
      "Благодарность наполняет меня теплом",
      "Я ценю то, что у меня уже есть",
    ],
  },
  {
    id: "joy",
    related: ["gratitude", "present", "energy"],
    title: "Радость",
    keys: ["радост", "счаст", "весел", "удовольств", "наслажд", "улыб", "хорошее настроен", "настроени", "грусть", "грустн", "тоск", "уныни", "депресс", "плохо на душе"],
    phrases: [
      "Я выбираю радость",
      "Я позволяю себе быть {счастливым|счастливой}",
      "Мне легко улыбаться",
      "Я нахожу радость в простых вещах",
      "Сегодня будет хороший день",
      "Я наполняю свой день тем, что люблю",
      "Счастье — это то, что я чувствую уже сейчас",
      "Я бережно проживаю и грусть, и радость",
    ],
  },
  {
    id: "letgo",
    related: ["calm", "selfworth", "change"],
    title: "Отпускание",
    keys: ["отпуст", "отпуска", "обид", "простить", "прощ", "прошл", "бывш", "забыть", "забыва", "злост", "=зло", "гнев", "злюсь", "раздраж", "ненавиж", "травм", "разочаров"],
    phrases: [
      "Я отпускаю прошлое и живу сейчас",
      "Я прощаю, чтобы освободить себя",
      "Обиды больше не управляют мной",
      "Я благодарю прошлое за опыт и иду дальше",
      "Мои чувства проходят, как волны",
      "Я освобождаю место для нового",
      "Я выбираю мир в своём сердце",
      "Я позволяю себе чувствовать и отпускать",
      "Каждый день я становлюсь легче",
    ],
  },
  {
    id: "change",
    related: ["fear", "confidence", "letgo"],
    title: "Перемены",
    keys: ["перемен", "измени", "новая жизнь", "новую жизнь", "начать сначала", "переезд", "переехать", "кризис", "неопределен", "выбор", "решени"],
    phrases: [
      "Я с доверием встречаю перемены",
      "Новое приходит в мою жизнь вовремя",
      "Я легко адаптируюсь",
      "Каждое окончание открывает новое начало",
      "Я принимаю решения, которые меня поддерживают",
      "Я {готов|готова} к лучшей версии своей жизни",
      "Я доверяю себе в выборе",
      "Перемены делают меня сильнее и мудрее",
    ],
  },
  {
    id: "home",
    related: ["gratitude", "calm", "money"],
    title: "Дом",
    keys: ["квартир", "дом", "жиль", "ипотек", "ремонт", "уют", "переезд"],
    phrases: [
      "Мой дом — место силы и покоя",
      "Я создаю уют вокруг себя",
      "У меня есть дом, о котором я мечтаю",
      "В моём доме легко дышится",
      "Я с радостью возвращаюсь домой",
      "Пространство вокруг меня поддерживает меня",
    ],
  },
  {
    id: "travel",
    related: ["joy", "calm", "change"],
    title: "Путешествия",
    keys: ["путешеств", "поездк", "отпуск", "море", "страны", "перелет", "перелёт", "самолет", "самолёт", "отдых"],
    phrases: [
      "Я открываю мир с радостью",
      "Мои поездки проходят легко и безопасно",
      "Я позволяю себе отдыхать по-настоящему",
      "Новые места наполняют меня вдохновением",
      "Я путешествую часто и с удовольствием",
      "В дороге я {спокоен|спокойна} и {расслаблен|расслаблена}",
    ],
  },
  {
    id: "present",
    related: ["calm", "gratitude", "joy"],
    title: "Здесь и сейчас",
    keys: ["осознан", "медитац", "здесь и сейчас", "момент", "присутств", "дыхани", "дышать", "гармони", "баланс", "внутренн", "духовн"],
    phrases: [
      "Я здесь, я дышу, я живу",
      "Этот момент — всё, что мне нужно",
      "Я в гармонии с собой и миром",
      "Я замечаю своё дыхание и возвращаюсь в себя",
      "Мой ум ясен, а сердце открыто",
      "Я присутствую в каждом мгновении",
      "Внутри меня покой и равновесие",
    ],
  },
  {
    id: "morning",
    related: ["energy", "joy", "goals"],
    title: "Утро",
    keys: ["утр", "проснул", "начало дня", "новый день", "день начина", "сегодня"],
    phrases: [
      "Сегодня я встречаю день с открытым сердцем",
      "Этот день полон возможностей",
      "Я просыпаюсь с благодарностью",
      "Я выбираю, каким будет мой день",
      "Сегодня у меня всё получается",
      "Я начинаю день спокойно и уверенно",
    ],
  },
  {
    id: "general",
    related: [],
    title: "Мой настрой",
    keys: [],
    phrases: [
      "Я здесь, я дышу, я живу",
      "Я в гармонии с собой и миром",
      "Я выбираю спокойствие и радость",
      "Всё, что мне нужно, приходит ко мне вовремя",
      "Я благодарю этот день",
      "Я доверяю своему пути",
      "Я люблю и принимаю себя",
      "Моя жизнь наполнена смыслом",
      "Я позволяю себе хорошее",
      "Каждый день я становлюсь лучше",
    ],
  },
];

/** Примеры запросов для стартового экрана: каждый раз показываем случайные */
export const INTENT_EXAMPLES = [
  "Хочу спокойно засыпать и высыпаться",
  "Хочу перестать тревожиться перед выступлениями",
  "Хочу больше зарабатывать и не бояться просить повышение",
  "Хочу полюбить себя и перестать себя критиковать",
  "Хочу встретить своего человека",
  "Аффирмации на собеседование",
  "Хочу больше энергии по утрам",
  "Хочу перестать откладывать дела на потом",
  "Хочу чувствовать себя красивой",
  "Хочу научиться говорить «нет»",
  "Хочу спокойно сдать экзамен",
  "Хочу, чтобы клиенты находили меня сами",
  "Хочу отпустить обиду и жить дальше",
  "Хочу быть терпеливой мамой",
  "Хочу заниматься спортом регулярно",
  "Хочу верить в себя",
  "Хочу больше радости в каждом дне",
  "Хочу уверенно чувствовать себя на работе",
  "Хочу перестать сравнивать себя с другими",
  "Хочу справиться с переменами",
  "Хочу вдохновения для творчества",
  "Хочу гармонии в отношениях",
  "Хочу заботиться о своём теле",
  "Хочу больше смелости",
];
